import React, { useState, useEffect, useRef } from 'react'
import { Card } from 'components'
import { useLoading, useModal, usePersistForm, useToggle } from 'hooks'
import moment from 'moment'
import {
  WalletNGTServices,
  ISearchWalletResponse,
  ISearchWalletParams
} from 'services/NGT/Wallet'
import { SPACING } from 'utils'
import { FilterCre, TableSearchCre } from './components'
import { ViewContractModal } from '../ViewContractModal/ViewContractModal'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

const SCHEMA = yup.object({
  CdContrato: yup.string().notRequired().nullable(),
  NmFornecedor: yup.string().notRequired().nullable(),
  CdCRE: yup.string().notRequired().nullable(),
  NuAnoMesCompetenciaInicial: yup
    .date()
    .nullable()
    .typeError('Digite uma data válida'),
  NuAnoMesCompetenciaFinal: yup
    .date()
    .min(
      yup.ref('NuAnoMesCompetenciaInicial'),
      'Competência final deve ser maior que a competència inicial.'
    )
    .nullable()
    .typeError('Digite uma data válida'),
  NmAnalista: yup.string().notRequired().nullable(),
  DtAnalise: yup
    .date()
    .notRequired()
    .nullable()
    .typeError('Digite uma data válida'),
  DtUltimaAnalise: yup
    .date()
    .notRequired()
    .nullable()
    .typeError('Digite uma data válida')
    .min(
      yup.ref('DtAnalise'),
      'Data  Ult. Análise deve ser maior que Data Análise.'
    ),
  FlMobilizacao: yup.bool().notRequired().nullable(),
  FlMedicao: yup.bool().notRequired().nullable(),
  FlDenuncia: yup.bool().notRequired().nullable(),
  FlProjeto: yup.bool().notRequired().nullable(),
  FlAlerta: yup.bool().notRequired().nullable(),
  flTodos: yup.bool().notRequired().nullable()
})

const CreSearch = () => {
  const { setLoading } = useLoading()
  const { showModal } = useModal()

  const [walletSearchResult, setWalletSearchResult] = useState<
    ISearchWalletResponse | undefined
  >()
  const [page, setPage] = useState<number>(1)
  const isFirstMount = useRef<boolean>(true)
  const [dateErrors, setDateErrors] = useState<any>(null)
  const [rowsPerPage, setRowsPerPage] = useState<number>(15)
  const [openViewContractModal, toggleViewContractModal] = useToggle()
  const [selectedContractId, setSelectedContractId] = useState(0)

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ISearchWalletParams>({
    resolver: yupResolver(SCHEMA),
    defaultValues: {
      DtAnalise: null,
      DtUltimaAnalise: null,
      NuAnoMesCompetenciaInicial: null,
      NuAnoMesCompetenciaFinal: null,
      flTodos: false,
      FlAlerta: false,
      FlDenuncia: false,
      FlMedicao: false,
      FlMobilizacao: false,
      FlProjeto: false
    }
  })

  usePersistForm('ngt_cre_filter', {
    setValue,
    watch
  })

  const FlTodos = watch('flTodos')
  const NuAnoMesCompetenciaFinal = watch('NuAnoMesCompetenciaFinal')
  const NuAnoMesCompetenciaInicial = watch('NuAnoMesCompetenciaInicial')
  const DtAnalise = watch('DtAnalise')
  const DtUltimaAnalise = watch('DtUltimaAnalise')

  const isSearchDisabled = () => {
    return (
      (NuAnoMesCompetenciaFinal
        ? !moment(NuAnoMesCompetenciaFinal).isValid()
        : false) ||
      (NuAnoMesCompetenciaInicial
        ? !moment(NuAnoMesCompetenciaInicial).isValid()
        : false) ||
      (DtAnalise ? !moment(DtAnalise).isValid() : false) ||
      (DtUltimaAnalise ? !moment(DtUltimaAnalise).isValid() : false) ||
      dateErrors?.CompetenciaInicial ||
      dateErrors?.CompetenciaFinal
    )
  }

  const handleSearch = (
    isClick?: boolean,
    isClear: Boolean = false,
    form: ISearchWalletParams = {} as ISearchWalletParams
  ) => {
    setLoading('loading')

    const objEnvio: ISearchWalletParams = isClear
      ? {
        ...form,
        CdCRE: '',
        CdContrato: '',
        DtAnalise: '',
        DtUltimaAnalise: '',
        FlAlerta: null,
        FlDenuncia: null,
        FlMedicao: null,
        FlMobilizacao: null,
        FlProjeto: null,
        NmAnalista: '',
        NmFornecedor: '',
        NuAnoMesCompetenciaFinal: '',
        NuAnoMesCompetenciaInicial: '',
        PageNumber: page,
        PageSize: rowsPerPage,
        flTodos: true
      }
      : {
        ...(form.NmAnalista && {
          NmAnalista: form.NmAnalista
        }),
        ...(form.NmFornecedor && {
          NmFornecedor: form.NmFornecedor
        }),
        ...(form.CdContrato && {
          CdContrato: form.CdContrato
        }),
        ...(form.CdCRE && {
          CdCRE: form.CdCRE
        }),
        ...(form.flTodos !== false && {
          flTodos: form.flTodos
        }),
        ...(form.FlAlerta !== false && {
          FlAlerta: form.FlAlerta
        }),
        ...(form.FlDenuncia !== false && {
          FlDenuncia: form.FlDenuncia
        }),
        ...(form.FlMedicao !== false && {
          FlMedicao: form.FlMedicao
        }),
        ...(form.FlMobilizacao !== false && {
          FlMobilizacao: form.FlMobilizacao
        }),
        ...(form.FlProjeto !== false && {
          FlProjeto: form.FlProjeto
        }),
        ...(form.DtAnalise && {
          DtAnalise: moment(form.DtAnalise).toISOString()
        }),
        ...(form.DtUltimaAnalise && {
          DtUltimaAnalise: moment(form.DtUltimaAnalise).toISOString()
        }),
        ...(form.NuAnoMesCompetenciaInicial && {
          NuAnoMesCompetenciaInicial: moment(
            form.NuAnoMesCompetenciaInicial
          ).format('YYYYMM')
        }),
        ...(form.NuAnoMesCompetenciaFinal && {
          NuAnoMesCompetenciaFinal: moment(
            form.NuAnoMesCompetenciaFinal
          ).format('YYYYMM')
        }),
        PageNumber: page,
        PageSize: rowsPerPage
      }

    objEnvio['flTodos'] === false ? (objEnvio['flTodos'] = null) : true

    WalletNGTServices()
      .SearchWallet(objEnvio)
      .then((response) => {
        if (response.status === 200) {
          const { data: tableData } = response

          setWalletSearchResult(tableData)
        }

        setLoading('idle')
      })
      .catch(() => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível obter os resultados da pesquisa',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })

    if (isClick) {
      setPage(1)
    }
  }

  const handleClear = () => {
    reset()
    handleSearch(true, true)
  }

  useEffect(() => {
    if (!isFirstMount.current) {
      handleSearch(false, false, control?._formValues as ISearchWalletParams)
    } else {
      isFirstMount.current = false
    }
  }, [page, rowsPerPage])

  const handleViewContractModal = (contractId: number) => {
    setSelectedContractId(contractId)
    toggleViewContractModal()
  }

  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        borderRadius={`0px 10px 0px 0px`}
      >
        <FilterCre
          isSearchDisabled={isSearchDisabled()}
          dateErrors={dateErrors}
          setDateErrors={setDateErrors}
          handleSearch={handleSearch}
          handleClear={handleClear}
          control={control}
          errors={errors}
          handleSubmit={handleSubmit}
        />
        <TableSearchCre
          isSearchDisabled={isSearchDisabled()}
          selectedContractInfo={handleViewContractModal}
          tableData={walletSearchResult?.items || []}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          pageCount={walletSearchResult?.pageCount}
          page={page}
          setPage={setPage}
        />
        <ViewContractModal
          showModal={openViewContractModal}
          toggleModal={toggleViewContractModal}
          contractID={selectedContractId}
        />
      </Card>
    </>
  )
}

export default CreSearch
